/**
 * YouTube Thumbnail Downloader — pure helpers.
 *
 * Thumbnails live at predictable URLs (`img.youtube.com/vi/<id>/<quality>.jpg`),
 * so no API key is needed. These functions extract the 11-char video id from any
 * YouTube URL shape and build the per-resolution thumbnail URLs.
 */

const ID_RE = /^[A-Za-z0-9_-]{11}$/;
const PATH_PREFIXES = new Set(["shorts", "embed", "live", "v"]);
const YT_HOSTS = new Set([
  "youtube.com",
  "m.youtube.com",
  "music.youtube.com",
  "youtube-nocookie.com",
]);

/**
 * Extract a YouTube video id from a URL or raw id. Handles watch?v=, youtu.be/,
 * /shorts/, /embed/, /live/, /v/ and extra query params. Returns null if none.
 */
export function extractVideoId(input: string): string | null {
  if (!input) return null;
  const s = input.trim();
  if (ID_RE.test(s)) return s;

  let url: URL | null = null;
  try {
    url = new URL(s.includes("://") ? s : `https://${s}`);
  } catch {
    url = null;
  }

  if (url) {
    const host = url.hostname.replace(/^www\./, "");
    if (host === "youtu.be") {
      const id = url.pathname.split("/").filter(Boolean)[0];
      if (id && ID_RE.test(id)) return id;
    }
    if (YT_HOSTS.has(host)) {
      const v = url.searchParams.get("v");
      if (v && ID_RE.test(v)) return v;
      const parts = url.pathname.split("/").filter(Boolean);
      if (
        parts.length >= 2 &&
        PATH_PREFIXES.has(parts[0]) &&
        ID_RE.test(parts[1])
      ) {
        return parts[1];
      }
    }
  }

  // Last resort: find an id immediately after a known delimiter.
  const m = s.match(
    /(?:v=|vi\/|embed\/|shorts\/|live\/|youtu\.be\/)([A-Za-z0-9_-]{11})/,
  );
  return m ? m[1] : null;
}

export interface ThumbnailQuality {
  /** YouTube filename without extension, e.g. "maxresdefault". */
  key: string;
  width: number;
  height: number;
}

/** Available thumbnail resolutions, largest first. */
export const THUMBNAIL_QUALITIES: ThumbnailQuality[] = [
  { key: "maxresdefault", width: 1280, height: 720 },
  { key: "sddefault", width: 640, height: 480 },
  { key: "hqdefault", width: 480, height: 360 },
  { key: "mqdefault", width: 320, height: 180 },
  { key: "default", width: 120, height: 90 },
];

const VALID_QUALITIES = new Set(THUMBNAIL_QUALITIES.map((q) => q.key));

export function isValidQuality(key: string): boolean {
  return VALID_QUALITIES.has(key);
}

/** Direct YouTube CDN url for a given video id + quality. */
export function thumbnailUrl(id: string, quality: string): string {
  return `https://img.youtube.com/vi/${id}/${quality}.jpg`;
}

export interface Thumbnail extends ThumbnailQuality {
  url: string;
}

/** All thumbnail URLs for a video id, largest first. */
export function buildThumbnails(id: string): Thumbnail[] {
  return THUMBNAIL_QUALITIES.map((q) => ({
    ...q,
    url: thumbnailUrl(id, q.key),
  }));
}
